interface CachedImage {
  url: string;
  timestamp: number;
  data: string;
}

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 días en milisegundos
const CACHE_PREFIX = 'ship_image_';

// Añadir compresión de imágenes antes de cachear
const compressImage = async (blob: Blob): Promise<Blob> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      
      // Reducir tamaño si es muy grande
      let width = img.width;
      let height = img.height;
      const MAX_SIZE = 800;
      
      if (width > MAX_SIZE || height > MAX_SIZE) {
        if (width > height) {
          height = (height / width) * MAX_SIZE;
          width = MAX_SIZE;
        } else {
          width = (width / height) * MAX_SIZE;
          height = MAX_SIZE;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => resolve(blob!),
        'image/webp',
        0.8
      );
    };
    img.src = URL.createObjectURL(blob);
  });
};

export const ImageCache = {
  async getImage(url: string): Promise<string> {
    const cachedImage = this.getFromCache(url);
    if (cachedImage) {
      return cachedImage;
    }

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const compressedBlob = await compressImage(blob);
      const base64 = await this.blobToBase64(compressedBlob);
      this.saveToCache(url, base64);
      return base64;
    } catch (error) {
      return url;
    }
  },

  getFromCache(url: string): string | null {
    const key = CACHE_PREFIX + this.hashUrl(url);
    const cached = localStorage.getItem(key);
    
    if (!cached) return null;

    const { data, timestamp }: CachedImage = JSON.parse(cached);
    const now = Date.now();

    if (now - timestamp > CACHE_DURATION) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  },

  saveToCache(url: string, base64Data: string) {
    const key = CACHE_PREFIX + this.hashUrl(url);
    const data: CachedImage = {
      url,
      timestamp: Date.now(),
      data: base64Data
    };

    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      this.clearOldCache();
      try {
        localStorage.setItem(key, JSON.stringify(data));
      } catch (error) {
        // Silently continue if cache save fails
      }
    }
  },

  clearOldCache() {
    const keys = Object.keys(localStorage);
    const imageKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
    const cachedImages = imageKeys
      .map(key => {
        const data = localStorage.getItem(key);
        return {
          key,
          timestamp: data ? JSON.parse(data).timestamp : 0
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    const deleteCount = Math.ceil(cachedImages.length * 0.2);
    cachedImages.slice(0, deleteCount).forEach(item => {
      localStorage.removeItem(item.key);
    });
  },

  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  hashUrl(url: string): string {
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      const char = url.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }
}; 